/**
 * Telling what somebody wrote now from the mail they wrote it under.
 *
 * A reply carries the mail it answers: on the production mailbox 90 % of every
 * inbound character is our own mail quoted back, so the thread reads as a wall
 * the coordinator scrolls past, and the AI prompt pays for that mail twice —
 * once as the copy we sent, once as the quote of it in the reply underneath.
 *
 * This finds the seam and nothing else. It never rewrites a body: the answer is
 * one index into the string the mail server handed over, so
 * `body.slice(0, at) + body.slice(at)` is the body, always. That is what makes
 * it safe to run on a record — a boundary in the wrong place is a display the
 * reader opens with one click, never a reply that stopped existing.
 *
 * Which is also why every rule here refuses rather than guesses. The shapes
 * below are the ones a real mailbox produces; anything else means `'none'`, and
 * `'none'` means the mail is shown whole, exactly as it was before this file.
 */

/** Which rule found the seam. `'none'` is a body with no quote to hide. */
export type QuoteBoundary = 'quote-prefix' | 'attribution' | 'separator' | 'none';

export interface QuotedSplit {
  /** Where the quoted history begins — `body.length` when there is none. */
  at: number;
  /** What the sender wrote now, without the blank lines above the seam. */
  reply: string;
  /** The quoted history from `at`, verbatim; `''` when there is none. */
  quoted: string;
  by: QuoteBoundary;
}

/**
 * The verb an attribution line carries, in the languages this mailbox has
 * actually seen plus their obvious neighbours.
 *
 * Slovenian clients write `napisal`, `napisala`, `napisal/-a` and `napisal(a)`
 * depending on the client and on whether it knows the sender's gender, so the
 * stem is matched with the suffix optional — and iPhone Mail's Slovenian says
 * `zapisala` where Gmail's says `napisala`, which is one letter and a whole
 * client's worth of mail. French writes `a écrit :` — with a space before the
 * colon, which is why the colon is never matched tight.
 */
const VERB_LAST = 'wrote|[nz]apisa[a-z]*(?:\\s*/\\s*-?a)?(?:\\s*\\(-?a\\))?|a écrit|ha scritto|escribió|escreveu';

/**
 * German, Dutch and the Nordic languages put the verb before the sender —
 * `… um 10:04 Uhr schrieb Ana <ana@example.si>:` — so for those the colon is
 * the end of the address rather than of the verb. An address holds no colon,
 * which is what keeps the wildcard from running past the line it belongs to.
 */
const VERB_FIRST = '(?:schrieb|schreef|skrev)\\b[^:]{0,150}';

/**
 * An attribution ends on the verb, or on the sender the verb introduced —
 * and then on a colon, or on the dashes Zoho closes its own with:
 * `---- On Mon, 07 Sep 2026 … wrote ---`. That one matters more than it looks:
 * every mail the coordinator writes from the predal is read back out of the
 * Sent folder, so it is half the corpus this exists to trim.
 */
const ATTRIBUTION_END = new RegExp(`(?:${VERB_LAST}|${VERB_FIRST})(?:\\s*:|\\s+-{2,})\\s*$`, 'i');

/**
 * What tells an attribution from a sentence.
 *
 * "kot mi je Ana napisala:" ends the same way and is somebody's prose. A real
 * attribution carries the mail it points at — the date it was sent, or the
 * address it came from — so one of those is required before the line counts.
 */
const HAS_DATE = /\b(?:19|20)\d{2}\b/;
const HAS_ADDRESS = /@[a-z0-9.-]+\.[a-z]{2,}/i;

/**
 * How many lines an attribution may be wrapped over.
 *
 * Gmail breaks it in two more often than not — the date and the sender's name
 * on one line, `<address> napisala:` on the next — so matching a single line
 * finds the seam a line too low and leaves the date sitting in the thread as
 * the last thing the volunteer appears to have written.
 */
const ATTRIBUTION_LINES = 3;

/** The longest an attribution can be before it is somebody's paragraph. */
const ATTRIBUTION_CHARS = 250;

/** A client's own rule between the reply and what it is replying to. */
const SEPARATOR =
  /^\s*[-—_]{2,}\s*(?:original message|originalna sporočila|izvorno sporočilo|forwarded message|posredovano sporočilo|weitergeleitete nachricht|message d'origine|mensaje original)\s*[-—_]{2,}\s*$/i;

/** Outlook's rule above the headers it reprints. Meaningless without them. */
const OUTLOOK_RULE = /^\s*_{8,}\s*$/;

/**
 * The first line of the header block Outlook reprints instead of quoting.
 *
 * `de` and `da` are the Romance "From:", and they are left out on purpose:
 * two letters and a colon is a shape ordinary writing makes by accident, and
 * this rule has no mail in the box asking for them.
 */
const HEADER_FROM = /^\s*(?:from|od|von)\s*:\s*\S/i;

/**
 * One of the lines that must follow it under Outlook's rule, or `From:` was
 * somebody's prose.
 */
const HEADER_REST = /^\s*(?:sent|poslano|date|datum|to|za|an|à|subject|zadeva|betreff|objet)\s*:/i;

/**
 * How many of those must follow before the block is a block.
 *
 * Outlook reprints four — From, Sent, To, Subject — so requiring two costs
 * nothing there and keeps a volunteer's own small list out of it: somebody
 * writing "Od: Ljubljana" over "Za: Kranjsko Goro" has written two lines of a
 * message, and treating them as a header block would fold the rest of what
 * they said and take it out of the AI prompt.
 */
const HEADER_MIN_REST = 2;

/** How many non-blank lines below `From:` those may be looked for in. */
const HEADER_LINES = 8;

/**
 * And how far down the body, blank lines counted.
 *
 * A block's own lines stand together, however a client spaces them — four of
 * them at Zoho's widest is some thirty lines. Past that they are not one
 * block, and without the bound a labelled line anywhere below a `From:` would
 * answer for it: `reprintAt` ends the search where it finds one, and a mail
 * whose quote then goes unfolded costs the reader and the prompt the wall this
 * file exists to fold away.
 */
const HEADER_SPAN = 40;

/** A quoted line, allowing the few leading spaces a client sometimes adds. */
const QUOTED_LINE = /^\s{0,3}>/;

/**
 * How much of what follows a seam must itself be quoted, by weight of text.
 *
 * A signature under the quote is ordinary and rides along with it, which costs
 * nothing — it is ballast either way. A reply typed *below* the quote is not
 * ballast, and this is what keeps the seam from swallowing one.
 *
 * Weighed in characters rather than lines, because that is what tells the two
 * apart: a signature is a hundred characters under a quote of two thousand,
 * where somebody answering below the quote writes more than they quoted. By
 * line count both are "a few lines", and a short quote would lose its
 * signature the same way a real answer does.
 */
const QUOTED_SHARE = 0.7;

interface Line {
  text: string;
  /** Offset of the line's first character in the body. */
  at: number;
}

function linesOf(body: string): Line[] {
  const out: Line[] = [];
  let at = 0;
  for (const text of body.split('\n')) {
    out.push({ text, at });
    at += text.length + 1;
  }
  return out;
}

/**
 * The seam at line `i`, if the lines from there read as an attribution.
 *
 * Widened a line at a time because the wrap is a client's decision, not a
 * grammar: what has to hold is that the window ends on the verb, carries a date
 * or an address, and is short enough to be a header rather than a paragraph. A
 * blank line ends the window — an attribution is never written across one.
 */
function attributionAt(lines: readonly Line[], i: number): number {
  // The window starts where the attribution does. Allowing it to open on a
  // blank line would put the seam a line above the attribution and hand the
  // reply a trailing blank — and, worse, let a two-line attribution match from
  // the blank line before it, which is one line too high in the body.
  if (lines[i]!.text.trim() === '') return 0;
  let joined = '';
  for (let n = 0; n < ATTRIBUTION_LINES && i + n < lines.length; n += 1) {
    const line = lines[i + n]!.text;
    if (n > 0 && line.trim() === '') return 0;
    joined = n === 0 ? line : `${joined} ${line.trim()}`;
    if (joined.length > ATTRIBUTION_CHARS) return 0;
    // Leading `>` on the whole thing is a quoted attribution, one level down;
    // it is a seam as much as an unquoted one, and it is how a reply to a
    // reply arrives.
    const text = joined.replace(/^\s*>+\s*/, '').trim();
    if (!ATTRIBUTION_END.test(text)) continue;
    if (!HAS_DATE.test(text) && !HAS_ADDRESS.test(text)) return 0;
    // Line `i` opens the attribution only if the attribution needs it. Where a
    // client leaves no blank line above — iPhone Mail, Outlook mobile — a
    // window opened two lines up still *ends* on the verb, and the seam would
    // then land on the volunteer's sign-off and eat the sentence above it.
    // The date is what settles it: an attribution carries exactly one, so if
    // the lines below `i` already hold it, the attribution starts below `i`.
    if (n > 0 && HAS_DATE.test(joinedFrom(lines, i + 1, i + n))) return 0;
    return n + 1;
  }
  return 0;
}

/** The first line from `from` with something on it, or `-1`. */
function nextNonBlank(lines: readonly Line[], from: number): number {
  for (let n = from; n < lines.length; n += 1) {
    if (lines[n]!.text.trim() !== '') return n;
  }
  return -1;
}

/** Lines `from`..`to` as the one logical line a client wrapped them from. */
function joinedFrom(lines: readonly Line[], from: number, to: number): string {
  const parts: string[] = [];
  for (let n = from; n <= to; n += 1) parts.push(lines[n]!.text.trim());
  return parts.join(' ');
}

/**
 * The header block Outlook reprints under its own rule, where another client
 * would quote. Answers how many lines the block runs to, `0` when line `i`
 * does not open one.
 */
function headersAt(lines: readonly Line[], i: number): number {
  // Asked about the line past the end when Outlook's rule is the last one in
  // the body — there is no block under it, and no throw either.
  const first = lines[i];
  if (!first || !HEADER_FROM.test(first.text)) return 0;
  let found = 0;
  let last = 0;
  let seen = 0;
  // Counted without the blank lines between them: a client that writes its
  // plain part out of the HTML the sender typed puts one, and sometimes
  // several, between every line of the block.
  for (let n = i + 1; n < lines.length && n - i <= HEADER_SPAN; n += 1) {
    const text = lines[n]!.text;
    if (text.trim() === '') continue;
    seen += 1;
    if (seen > HEADER_LINES) break;
    if (!HEADER_REST.test(text)) continue;
    found += 1;
    last = n - i;
  }
  return found >= HEADER_MIN_REST ? last + 1 : 0;
}

/**
 * The first line of the block Zoho reprints over the mail it answers: `Od:`
 * and the address that mail came from.
 */
const ZOHO_FROM = /^\s*od\s*:.*@[a-z0-9.-]+\.[a-z]{2,}/i;

/**
 * The lines that follow it, in the order Zoho's Slovenian web client writes
 * them. `Kp:` is there only when the answered mail was copied to somebody.
 */
const ZOHO_REST: readonly { label: RegExp; optional?: true; recipients?: true }[] = [
  { label: /^\s*v\s*:/i, recipients: true },
  { label: /^\s*kp\s*:/i, optional: true, recipients: true },
  { label: /^\s*datum\s*:/i },
  { label: /^\s*zadeva\s*:/i },
];

/** A labelled line of any kind — which a wrapped recipient list is not. */
const LABELLED = /^\s*\p{L}+\s*:/u;

/**
 * How many non-blank lines below `Od:` the block may run to: its three or four
 * labelled lines, and the recipients a reply-all wraps under `V:` and `Kp:`.
 *
 * Counted without the blank ones because those are the client's spacing, not
 * the block: an HTML-only reply puts one between each of its lines, and the
 * plain part Zoho writes out of the same HTML three to seven — a body is read
 * as it arrived, so a bound in physical lines would miss the widest of them.
 */
const ZOHO_BLOCK_LINES = 16;

/**
 * The block Zoho reprints with no rule above it, and how many lines it runs
 * to — `0` when line `i` does not open one.
 *
 * Only in the order Zoho writes it: `Od:` with an address, `V:`, `Kp:` when
 * there is one, `Datum:`, `Zadeva:`. Anything else between them — a line of
 * prose, another label, a label out of order — means it is not the block:
 * `Od:`, `Za:`, `Datum:` and `Zadeva:` are ordinary Slovenian words, and
 * somebody's own list of details can read a lot like one.
 */
function zohoBlockAt(lines: readonly Line[], i: number): number {
  if (!ZOHO_FROM.test(lines[i]!.text)) return 0;
  let step = 0;
  let inRecipients = false;
  let seen = 0;
  for (let n = i + 1; n < lines.length; n += 1) {
    const text = lines[n]!.text;
    if (text.trim() === '') continue;
    seen += 1;
    if (seen > ZOHO_BLOCK_LINES) return 0;
    // A recipient list Zoho wrapped: an address with no label of its own.
    if (inRecipients && !LABELLED.test(text) && HAS_ADDRESS.test(text)) continue;
    while (ZOHO_REST[step]?.optional && !ZOHO_REST[step]!.label.test(text)) step += 1;
    const want = ZOHO_REST[step];
    if (!want?.label.test(text)) return 0;
    inRecipients = want.recipients === true;
    step += 1;
    if (step === ZOHO_REST.length) return n - i + 1;
  }
  return 0;
}

/**
 * A reprinted header block of any shape, folded here or not — `0` when line
 * `i` opens none.
 *
 * What it is for is stopping. A block this file does not fold is still a
 * client saying "the answered mail starts here", and the text under it is
 * that mail: cutting anywhere below it — at the attribution the answered mail
 * carries, at a `>` in it — would put what we wrote into the volunteer's own
 * words, in the thread and in the prompt. So the search ends and the mail is
 * shown whole, which is what it did before any of these blocks were known.
 *
 * The address is what tells a reprint from somebody's own labelled list: a
 * reprint names the mail it introduces, where a volunteer listing their
 * details writes their name (see `attributionAt` for the same evidence).
 */
function reprintAt(lines: readonly Line[], i: number): number {
  return HAS_ADDRESS.test(lines[i]!.text) ? headersAt(lines, i) : 0;
}

/**
 * True when what follows the seam at line `i` reads as quoted history rather
 * than as somebody still writing.
 *
 * `marked` is a property of the whole body, not of the tail: it says whether
 * this mail quotes with `>` anywhere at all. Where it does not — an HTML-only
 * reply whose quotation was flattened to bare paragraphs, 2 of the 34 in the
 * mailbox — the tail is unmarked because nothing here is marked, which is no
 * evidence about anything, and the seam that was found is all there is. Where
 * it does, the marks are evidence in both directions: unmarked text below the
 * seam is somebody talking, not history.
 *
 * That is what catches a reply typed *under* the quote, in the one place it
 * can be caught. Folding one away would take a volunteer's answer out of the
 * thread and, worse, replace it with an elision in the AI prompt.
 *
 * Weighed from past the seam itself, which is why every rule reports how many
 * lines it spans: the seam is the history's own first line — Gmail wraps an
 * attribution over two, Outlook reprints four headers — and counting those as
 * unquoted would weigh the quote's introduction against the quote.
 *
 * A seam with nothing after it is history by default: there is no text below
 * to mistake for somebody writing. See `QUOTED_SHARE`.
 */
function tailIsHistory(lines: readonly Line[], from: number, marked: boolean): boolean {
  if (!marked) return true;
  let quoted = 0;
  let written = 0;
  for (let n = from; n < lines.length; n += 1) {
    const text = lines[n]!.text.trim();
    if (text === '') continue;
    written += text.length;
    if (QUOTED_LINE.test(text)) quoted += text.length;
  }
  if (written === 0) return true;
  return quoted / written >= QUOTED_SHARE;
}

export interface SplitOptions {
  /**
   * The mail was written from the predal and read back out of its Sent folder
   * — `direction = 'out'`. Only those carry the block Zoho reprints with no
   * rule above it, so only those are searched for one.
   */
  fromPredal?: boolean;
}

/**
 * The body, split where the quoted history starts.
 *
 * Only ever call this on a mail somebody else's client wrote — `source` is
 * `'imap'`. Our own rendered mail and a coordinator's typed note carry no
 * quote, so running it there is all risk and no benefit.
 *
 * A seam at the very top is not a seam: a bare forward is all quote, and hiding
 * the whole of it would leave the reader an empty message. That is `'none'`,
 * and so is a body no rule recognises.
 */
export function splitQuoted(body: string, { fromPredal = false }: SplitOptions = {}): QuotedSplit {
  const whole: QuotedSplit = { at: body.length, reply: body.trimEnd(), quoted: '', by: 'none' };
  const lines = linesOf(body);
  // Asked of the body once, because it is a fact about the mail rather than
  // about any one seam in it — see `tailIsHistory`.
  const marked = lines.some((l) => QUOTED_LINE.test(l.text.trim()));

  for (let i = 0; i < lines.length; i += 1) {
    const seam = seamAt(lines, i, fromPredal);
    if (!seam) {
      // A reprinted block none of the rules folds — Outlook's without its
      // rule, Zoho's in a language this file does not read, any of them in a
      // volunteer's own mail — ends the search where it stands. See
      // `reprintAt`: what follows it is the answered mail, and a seam found
      // inside that would hand our words to the volunteer.
      if (reprintAt(lines, i) > 0) return whole;
      continue;
    }
    // A seam whose tail does not read as history ends the search rather than
    // deferring to whatever lies below it. Both halves of that matter. The
    // check itself is what keeps an answer typed under the quote out of the
    // fold — it applies to every rule, not only the `>` one. And *stopping*
    // is what keeps a refusal from promoting a worse seam further down: a
    // single stray `>` under a reprinted Outlook header block would otherwise
    // move the cut below our own mail, and the thread would show what we
    // wrote as what the volunteer said.
    if (!tailIsHistory(lines, i + seam.span, marked)) return whole;

    const at = lines[i]!.at;
    const reply = body.slice(0, at).trimEnd();
    // Nothing above the seam means the mail *is* the quote. Better whole.
    if (reply === '') return whole;
    return { at, reply, quoted: body.slice(at), by: seam.by };
  }

  return whole;
}

/**
 * The seam opening at line `i`, and how many lines it occupies — `null` where
 * none does. The span is what `tailIsHistory` weighs from.
 */
function seamAt(
  lines: readonly Line[],
  i: number,
  fromPredal: boolean,
): { by: QuoteBoundary; span: number } | null {
  const text = lines[i]!.text;
  if (SEPARATOR.test(text)) return { by: 'separator', span: 1 };

  // Outlook's rule means nothing on its own — it is the headers under it that
  // say this is a seam — so the two are one span. The block may stand a blank
  // line or two below the rule, which is how an HTML reply arrives.
  if (OUTLOOK_RULE.test(text)) {
    const start = nextNonBlank(lines, i + 1);
    const underRule = start === -1 ? 0 : headersAt(lines, start);
    if (underRule > 0) return { by: 'separator', span: start - i + underRule };
  }

  // Zoho reprints the answered mail's headers with no rule above them, and
  // every mail the coordinator writes from the predal comes back out of the
  // Sent folder wearing that block — the whole of the answered mail under it,
  // unmarked, read in the thread as something we had just written. Asked of
  // those mails alone: the block has never arrived in a volunteer's own mail,
  // and there the same lines could as well be their list of details, or
  // answers typed under a reprint, and folding either would take their words
  // out of the AI prompt.
  const reprinted = fromPredal ? zohoBlockAt(lines, i) : 0;
  if (reprinted > 0) return { by: 'separator', span: reprinted };

  const attribution = attributionAt(lines, i);
  if (attribution > 0) return { by: 'attribution', span: attribution };

  return QUOTED_LINE.test(text) ? { by: 'quote-prefix', span: 1 } : null;
}

/**
 * A stored message split into what its sender wrote now and what they quoted.
 * Only mail read off the server carries a quote to find — our own rendered mail
 * and a typed note never do — and a mail the coordinator wrote from the predal
 * is searched for Zoho's rule-less block. The thread, the AI prompt and the
 * turn rules all read a message through this.
 */
export function messageReply(m: { body: string; source: string; direction: string }): { reply: string; quoted: string } {
  if (m.source !== 'imap') return { reply: m.body, quoted: '' };
  const split = splitQuoted(m.body, { fromPredal: m.direction === 'out' });
  return { reply: split.reply, quoted: split.quoted };
}
